import {
  type Context,
  defaultTextMapSetter,
  propagation,
  ROOT_CONTEXT,
  type SpanContext,
  type TextMapGetter,
  trace,
  TraceFlags,
} from "@opentelemetry/api";
import {
  CompositePropagator,
  W3CBaggagePropagator,
  W3CTraceContextPropagator,
} from "@opentelemetry/core";

// Wire format for browser, Edge, and server. OTel owns W3C `traceparent` and
// `baggage`; this module only filters baggage entries and mints IDs.

// Anything header-shaped: fetch Headers, or an adapter over Temporal headers.
export type TextMapCarrier = {
  get(key: string): string | null | undefined;
};

// W3C baggage entries keyed by their wire name.
export type BaggageEntries = Readonly<Record<string, string>>;

export type ExtractedTraceContext = {
  spanContext?: SpanContext;
  baggage: BaggageEntries;
};

const propagator = new CompositePropagator({
  propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()],
});

const carrierGetter: TextMapGetter<TextMapCarrier> = {
  get: (carrier, key) => carrier.get(key) ?? undefined,
  keys: () => propagator.fields(),
};

// Web Crypto so the same code runs in Node, Edge, and the browser.
const getRandomHex = (byteLength: number): string => {
  const bytes = crypto.getRandomValues(new Uint8Array(byteLength));
  // All-zero IDs are invalid W3C IDs; the propagator would drop them.
  if (bytes.every((byte) => byte === 0)) {
    bytes[bytes.length - 1] = 1;
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join(
    ""
  );
};

export const createTraceId = (): string => getRandomHex(16);
export const createSpanId = (): string => getRandomHex(8);

export const createSpanContext = (traceId = createTraceId()): SpanContext => ({
  traceId,
  spanId: createSpanId(),
  traceFlags: TraceFlags.SAMPLED,
});

// Unknown keys on the wire are dropped here, not by the propagator.
const getBaggageEntries = (
  context: Context,
  baggageNames: readonly string[]
): BaggageEntries => {
  const baggage = propagation.getBaggage(context);
  return Object.fromEntries(
    baggageNames.flatMap((name) => {
      const entry = baggage?.getEntry(name);
      return entry ? [[name, entry.value]] : [];
    })
  );
};

// Pass no `baggageNames` to ignore incoming baggage entirely.
export const extract = ({
  carrier,
  baggageNames,
}: {
  carrier: TextMapCarrier;
  baggageNames: readonly string[];
}): ExtractedTraceContext => {
  const context = propagator.extract(ROOT_CONTEXT, carrier, carrierGetter);
  return {
    spanContext: trace.getSpanContext(context),
    baggage: getBaggageEntries(context, baggageNames),
  };
};

export const inject = ({
  spanContext,
  baggage,
}: {
  spanContext: SpanContext;
  baggage: BaggageEntries;
}): Record<string, string> => {
  const headers: Record<string, string> = {};
  propagator.inject(
    propagation.setBaggage(
      trace.setSpanContext(ROOT_CONTEXT, spanContext),
      propagation.createBaggage(
        Object.fromEntries(
          Object.entries(baggage).map(([name, value]) => [name, { value }])
        )
      )
    ),
    headers,
    defaultTextMapSetter
  );
  return headers;
};

// Browser and Edge entry points, where there is no store to read.
export const createTraceparent = (): string =>
  inject({ spanContext: createSpanContext(), baggage: {} }).traceparent;

export const getTraceIdFromTraceparent = (
  traceparent: string | null | undefined
): string | undefined =>
  extract({
    carrier: { get: (key) => (key === "traceparent" ? traceparent : null) },
    baggageNames: [],
  }).spanContext?.traceId;
